import { getApiBaseUrl } from '../../config/runtimeConfig';
import { sessionFetch } from '../../services/session';


function httpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw httpError(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Please check your connection and try again.');
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw httpError(
      typeof data?.message === 'string' ? data.message : 'Unable to complete the container request.',
      response.status,
    );
  }
  return data;
}

export function isUnpersistedContainerError(error) {
  return !error?.status || error.status === 404;
}

function toContainer(row) {
  if (!row) return null;
  return {
    id: Number(row.id),
    internal_reference: row.internal_reference || '',
    name: row.name || row.internal_reference || '',
    code: row.code || '',
    draft_date: row.draft_date || '',
    warehouse_id: row.warehouse_id == null ? null : Number(row.warehouse_id),
    warehouse_name: row.warehouse_name || '',
    warehouse_code: row.warehouse_code || '',
    profile_id: row.profile_id == null ? null : Number(row.profile_id),
    profile_name: row.profile_name || '',
    profile_size: row.profile_size || '',
    profile_type: row.profile_type || '',
    capacity: Number(row.capacity) || 0,
    column_capacity: Number(row.column_capacity || row.capacity) || 0,
    max_gross_payload: Number(row.max_gross_payload) || 0,
    unit_id: row.unit_id == null ? null : Number(row.unit_id),
    filled_cbm: Number(row.filled_cbm) || 0,
    remaining_cbm: Number(row.remaining_cbm) || 0,
    utilization_percent: Number(row.utilization_percent) || 0,
    carton_count: Number(row.carton_count) || 0,
    pallet_count: Number(row.pallet_count) || 0,
    net_weight: Number(row.net_weight) || 0,
    gross_weight: Number(row.gross_weight) || 0,
    status: row.status || 'Planning',
    status_label: row.status_label || 'Draft',
    status_tone: row.status_tone || 'draft',
  };
}

function buildListQuery(filters = {}) {
  const params = new URLSearchParams();
  if (filters.query) params.set('query', filters.query);
  if (filters.warehouse_id) params.set('warehouse_id', String(filters.warehouse_id));
  if (filters.status) params.set('status', filters.status);
  if (filters.date_range) params.set('date_range', filters.date_range);
  params.set('page', String(filters.page || 1));
  params.set('pageSize', String(filters.pageSize || 6));
  const qs = params.toString();
  return qs ? `/containers?${qs}` : '/containers';
}

const emptyLookups = {
  warehouses: [],
  profiles: [],
  units: [],
  next_reference: '',
  next_name: '',
  statuses: [],
  date_ranges: [
    { key: '', label: 'All Dates' },
    { key: 'last_7_days', label: 'Last 7 Days' },
    { key: 'last_30_days', label: 'Last 30 Days' },
    { key: 'last_90_days', label: 'Last 90 Days' },
    { key: 'this_year', label: 'This Year' },
  ],
};

const emptySummary = { total_draft: 0, planned: 0, ready_to_ship: 0, shipped: 0, customs_clearance: 0, delivered: 0, cancelled: 0 };

const containersInFlight = new Map();

export function listContainers(filters, options = {}) {
  const path = buildListQuery(filters);
  const key = `${getApiBaseUrl()}${path}`;
  if (!containersInFlight.has(key)) {
    containersInFlight.set(key, loadContainers(path, options).finally(() => { containersInFlight.delete(key); }));
  }
  return containersInFlight.get(key);
}

async function loadContainers(path, options) {
  try {
    const data = await request(path, options);
    return {
      containers: (data.containers || []).map(toContainer),
      total: Number(data.total) || 0,
      summary: {
        total_draft: Number(data.total_draft) || 0,
        ready_to_ship: Number(data.ready_to_ship) || 0,
        shipped: Number(data.shipped) || 0,
        customs_clearance: Number(data.customs_clearance) || 0,
        delivered: Number(data.delivered) || 0,
        planned: Number(data.planned) || 0,
        cancelled: Number(data.cancelled) || 0,
      },
      lookups: { ...emptyLookups, ...data.lookups },
    };
  } catch (error) {
    if (isUnpersistedContainerError(error)) {
      return { containers: [], total: 0, summary: emptySummary, lookups: emptyLookups };
    }
    throw error;
  }
}

export async function getContainer(id) {
  const data = await request(`/containers/${id}`);
  return data.container ? toContainer(data.container) : null;
}

export async function updateContainer(id, payload) {
  const data = await request(`/containers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const container = data.container;
  if (!container?.id) throw httpError('Container was not saved to the database.');
  return toContainer(container);
}

export async function deleteContainer(id) {
  await request(`/containers/${id}`, { method: 'DELETE' });
}

export async function createContainer(payload) {
  const data = await request('/containers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const container = data.container;
  if (!container?.id) throw httpError('Container draft was not saved to the database.');
  return toContainer(container);
}

const emptyGoodsSummary = {
  selected_weight: 0,
  selected_volume: 0,
  selected_quantity: 0,
  selected_cartons: 0,
  selected_sku_count: 0,
  remaining_payload: 0,
  remaining_volume: 0,
  payload_percent: 0,
  volume_percent: 0,
  free_payload_percent: 0,
  free_volume_percent: 0,
  max_payload: 0,
  capacity: 0,
};

function toGoodsItem(row) {
  if (!row) return null;
  return {
    id: Number(row.inventory_batch_product_id || row.id),
    item_id: row.item_id == null ? null : Number(row.item_id),
    inventory_batch_product_id: Number(row.inventory_batch_product_id || row.id),
    selection_mode: row.selection_mode || 'CARTON',
    selection_id: row.selection_id || '',
    product_count: Number(row.product_count) || row.products?.length || 1,
    inventory_id: row.inventory_id == null ? null : Number(row.inventory_id),
    product_id: row.product_id == null ? null : Number(row.product_id),
    inventory_batch_id: row.inventory_batch_id,
    batch_quantity: row.batch_quantity == null ? null : Number(row.batch_quantity),
    batch_cbm: row.batch_cbm == null ? null : Number(row.batch_cbm),
    batch_net_weight: row.batch_net_weight == null ? null : Number(row.batch_net_weight),
    batch_gross_weight: row.batch_gross_weight == null ? null : Number(row.batch_gross_weight),
    batch_number: row.batch_number || '',
    products: row.products || [],
    product_gross_weight: row.product_gross_weight == null ? null : Number(row.product_gross_weight),
    product_net_weight: row.product_net_weight == null ? null : Number(row.product_net_weight),
    product_name: row.product_name || '',
    sku: row.sku || '',
    customer_id: row.customer_id == null ? null : Number(row.customer_id),
    customer_name: row.customer_name || '',
    marko: row.marko || '',
    priority_id: row.priority_id == null ? null : Number(row.priority_id),
    priority_name: row.priority_name || '',
    quantity: Number(row.quantity) || 0,
    weight: Number(row.weight || row.gross_weight) || 0,
    cbm: Number(row.cbm) || 0,
    net_weight: Number(row.net_weight) || 0,
    gross_weight: Number(row.gross_weight || row.weight) || 0,
    warehouse_id: row.warehouse_id == null ? null : Number(row.warehouse_id),
    warehouse_name: row.warehouse_name || '',
    warehouse_code: row.warehouse_code || '',
    warehouse_city: row.warehouse_city || '',
    customer_logo: row.customer_logo || '',
    packaging_type: row.packaging_type || '',
    packaging_quantity: Number(row.packaging_quantity) || 0,
    total_carton_qty: Number(row.total_carton_qty) || 0,
    inventory_status: row.inventory_status || '',
  };
}

function toGoodsPayload(data) {
  const selected = [...new Map((data.selected || []).map(toGoodsItem).map(row => [row.selection_id || row.inventory_batch_product_id, row])).values()];
  const selectedIds = new Set(selected.map(row => row.selection_id || row.inventory_batch_product_id));
  const available = [...new Map((data.available || []).map(toGoodsItem).map(row => [row.selection_id || row.inventory_batch_product_id, row])).values()]
    .filter(row => !selectedIds.has(row.selection_id || row.inventory_batch_product_id));
  return {
    container: data.container ? toContainer(data.container) : null,
    available,
    selected,
    summary: { ...emptyGoodsSummary, ...data.summary },
    auto_added_count: Number(data.auto_added_count) || 0,
  };
}

const pendingGoods = new Map();
export function getContainerGoods(id) {
  const path = `/containers/${id}/goods`;
  const key = `${getApiBaseUrl()}${path}`;
  if (!pendingGoods.has(key)) {
    const pending = request(path).then(toGoodsPayload)
      .finally(() => pendingGoods.delete(key));
    pendingGoods.set(key, pending);
  }
  return pendingGoods.get(key);
}

const pendingAllocations = new Map();
export function autoAllocateContainerGoods(id) {
  if (!pendingAllocations.has(id)) {
    const pending = request(`/containers/${id}/goods/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ auto_fill: true }) })
      .then(toGoodsPayload).finally(() => pendingAllocations.delete(id));
    pendingAllocations.set(id, pending);
  }
  return pendingAllocations.get(id);
}

export async function addContainerGoods(id, inventoryBatchProductIds) {
  const data = await request(`/containers/${id}/goods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inventory_batch_product_ids: inventoryBatchProductIds }),
  });
  return toGoodsPayload(data);
}

export async function removeContainerGoods(id, inventoryBatchProductIds) {
  const data = await request(`/containers/${id}/goods/remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inventory_batch_product_ids: inventoryBatchProductIds }),
  });
  return toGoodsPayload(data);
}

export async function previewContainerGoods(id, selected, autoFill = false, excludeInventoryIds = []) {
  const data = await request(`/containers/${id}/goods/preview`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: selected.map(row => ({ inventory_batch_product_id: row.inventory_batch_product_id, selected_quantity: row.quantity })), auto_fill: autoFill, excludeInventoryIds }),
  });
  return toGoodsPayload(data);
}

export async function saveContainerGoods(id, selected) {
  const data = await request(`/containers/${id}/goods/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: selected ? JSON.stringify({ items: selected.map(row => ({ inventory_batch_product_id: row.inventory_batch_product_id, selected_quantity: row.quantity })) }) : undefined,
  });
  return toGoodsPayload(data);
}

export { emptyLookups, emptySummary, emptyGoodsSummary };
